import { Component, Input, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { Component as BrComponent, Document, Page, Reference } from "@bloomreach/spa-sdk";
import { Logger } from "@utils/logger";
import { getPageUrl } from "@utils/pagemodel-utils";
import { TranslateService } from "@components/shared/translate/translate.service";
import { AnalyticsService } from "@services/analytics.service";

const logger = Logger.getLogger("ImageCarouselComponent");

declare var bootstrap: any;

@Component({
  selector: "ft-image-carousel",
  templateUrl: "./image-carousel.component.html",
  styleUrls: ["./image-carousel.component.scss"],
})
export class ImageCarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() component!: BrComponent;
  @Input() page: Page;
  @Input() contentType: any;
  
  carouselInstance: any;
  private intervalId: any;

  constructor(
    private translateService: TranslateService,
    private analyticsService: AnalyticsService,
  ) {}

  ngOnInit(): void {
    // Component initialization logic
  }

  ngAfterViewInit(): void {
    this.initializeCarousels();
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.carouselInstance) {
      this.carouselInstance.dispose();
    }
  }

  get document() {
    const { document } = this.component?.getModels<DocumentModels>() || {};
    return document && this.page?.getContent<Document>(document);
  }

  get content() {
    return this.document?.getData();
  }

  get componentTitle() {
    return this.content?.componentTitle || '';
  }

  get imageCarouselSections() {
    return this.content?.imageCarouselSections || [];
  }

  get showNavigation() {
    return this.content?.showNavigation !== undefined ? this.content.showNavigation : true;
  }

  get showIndicators() {
    return this.content?.showIndicators !== undefined ? this.content.showIndicators : true;
  }

  get autoplayInterval() {
    const interval = this.content?.autoplayInterval;
    return interval !== undefined ? interval : 3000;
  }

  /**
   * Initialize all carousels in the sections
   */
  private initializeCarousels(): void {
    this.imageCarouselSections.forEach((section, index) => {
      const carouselId = `imageCarousel${index}`;
      const carouselElement = document.getElementById(carouselId);
      
      if (carouselElement) {
        const carousel = new bootstrap.Carousel(carouselElement, {
          interval: this.autoplayInterval > 0 ? this.autoplayInterval : false,
          ride: this.autoplayInterval > 0 ? 'carousel' : false,
          wrap: true
        });
      }
    });
  }

  /**
   * Get carousel images from a section
   */
  getCarouselImages(section: any) {
    return section?.carouselCompoundType || [];
  }

  /**
   * Get text content from a section
   */
  getTextContent(section: any) {
    return section?.textContent || {};
  }

  /**
   * Get text position for a section
   */
  getTextPosition(section: any) {
    return section?.textPosition || 'right';
  }

  /**
   * Check if section has text content
   */
  hasTextContent(section: any): boolean {
    const textContent = this.getTextContent(section);
    return !!(textContent?.title || textContent?.textContent);
  }

  /**
   * Get image URL from Widen document data
   */
  public getImageURL(data: any): string {
    if (data) {
      return JSON.parse(data)?.url || '';
    }
    return "";
  }

  /**
   * Get mobile image URL, fallback to desktop if not available
   */
  public getMobileImageURL(carouselItem: any): string {
    const mobileImage = carouselItem?.imageForMobile?.widenDocument;
    if (mobileImage) {
      return this.getImageURL(mobileImage);
    }
    // Fallback to desktop image
    return this.getDesktopImageURL(carouselItem);
  }

  /**
   * Get desktop image URL
   */
  public getDesktopImageURL(carouselItem: any): string {
    const desktopImage = carouselItem?.imageForDesktop?.widenDocument;
    return this.getImageURL(desktopImage);
  }

  /**
   * Get page URL from reference
   */
  public getPageUrl(ref: Reference | string) {
    return getPageUrl(this.page, ref);
  }

  /**
   * Get link URL from carousel item
   */
  getLinkUrl(carouselItem: any): string {
    return carouselItem?.LinkCompound?.linkCollection?.[0]?.url || '';
  }

  /**
   * Get link target from carousel item
   */
  getLinkTarget(carouselItem: any): string {
    return carouselItem?.LinkCompound?.linkCollection?.[0]?.target || '_self';
  }

  /**
   * Get link display text from carousel item
   */
  getLinkDisplayText(carouselItem: any): string {
    return carouselItem?.LinkCompound?.linkCollection?.[0]?.displayText || '';
  }

  /**
   * Check if carousel item has a link
   */
  hasLink(carouselItem: any): boolean {
    return !!this.getLinkUrl(carouselItem);
  }

  /**
   * Handle analytics for carousel item click
   */
  setAnalytics(title: string): void {
    try {
      this.analyticsService.trackEvent({
        event: "Event Image Carousel",
        category: "Image Carousel",
        action: 'Image click',
        label: title,
      });
      this.analyticsService.trackEvent({
        event: "Event Image Carousel",
        ft_event: 'ft_imageCarouselClick',
        category: "Image Carousel",
        action: 'Image click',
        label: title,
      });
    } catch (error) {
      logger.error('Error tracking analytics:', error);
    }
  }

  /**
   * Generate unique carousel ID for each section
   */
  getCarouselId(index: number): string {
    return `imageCarousel${index}`;
  }

  /**
   * Generate unique carousel target for controls
   */
  getCarouselTarget(index: number): string {
    return `#imageCarousel${index}`;
  }
}